import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Loader } from '@frame/ui-components/unified-search/loader'
import { Results } from '@frame/ui-components/unified-search/results'
import { SearchFooter } from '@frame/ui-components/unified-search/search-footer'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { H2 } from '@frame/ui-primitives/h2'
import { Input } from '@frame/ui-primitives/input'

import { EventScope, InternalEventType } from '@frame/lib/api/events/types'
import { buildSearchViewProps } from '@frame/lib/api/events/utils'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEmployerTitle } from '@frame/lib/hooks/useEmployerTitle'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { Item, ItemKind, useGetItems } from '@frame/lib/hooks/useGetItems'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'
import { useDebounceCallback } from '@react-hook/debounce'

const CONTAINER_ID = 'search-platform'
const MAX_LIMIT = 3

export function SearchPlatform() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const showExitPrompt = useShowExitPrompt()
  const task = useDirectTask()
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { handleEvent } = useEventHandler()
  const title = useHeaderTitle()
  const { getEmployerTitle } = useEmployerTitle()
  const [isMounted, setIsMounted] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const oneSecond = 1000
  const debouncedHandleEvent = useDebounceCallback(handleEvent, oneSecond)

  const { items, isLoading, isValidating } = useGetItems({
    query: searchQuery,
    kinds: [ItemKind.PLATFORM],
    limit: MAX_LIMIT,
  })

  const params = new URLSearchParams(search)
  const employerName: string = task?.employer_name || params.get('employerName') || ''

  useEffect(() => {
    const paramsOnMount = new URLSearchParams(location.search)
    const query: string = paramsOnMount.get('q') || ''
    if (!isMounted) {
      handleEvent(
        InternalEventType.PAYROLL_PLATFORM_SEARCH_VIEWED,
        buildSearchViewProps(EventScope.PLATFORM, task, query)
      )
    }
    setIsMounted(true)
  }, [task, handleEvent, isMounted])

  useEffect(() => {
    const paramsOnMount = new URLSearchParams(search)
    const query = paramsOnMount.get('q')

    if (query) {
      setSearchQuery(query)
      setHasSearched(true)
    }
  }, [search])

  function updateQuery(newQuery: string) {
    params.set('q', newQuery)
    if (task?.id) {
      navigate(`${pathname}?${params.toString()}`, { replace: true })
    } else {
      navigate(`${pathname}?${params.toString()}`, { replace: true })
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target
    setSearchQuery(value)
    updateQuery(value)
    setHasSearched(true)
    debouncedHandleEvent(
      InternalEventType.PAYROLL_PLATFORM_QUERIED,
      buildSearchViewProps(EventScope.PLATFORM, task, value)
    )
  }

  function goToConfirmEmployer(employerName: string, item?: Item) {
    const employerNameQuery = task?.is_external_employment ? task?.employer_name : employerName
    const params = new URLSearchParams(location.search)
    params.set('q', employerNameQuery || '')
    const route = task?.id ? `${routes.confirmEmployer}/${task?.id}` : routes.confirmEmployer
    navigate(`${route}?${params.toString()}`, {
      state: {
        fromPlatformSearch: true,
        item,
      },
    })
  }

  async function handleSelectItem({ item }: { item: Item }) {
    setSearchQuery(item?.name)
    params.set('q', item?.name)
    params.set('itemId', item?.id)
    navigate(`${pathname}?${params.toString()}`, { replace: true })
    goToConfirmEmployer(employerName, item)
  }

  function goBack() {
    navigate(-1)
  }

  const showLoader = searchQuery && (isLoading || isValidating)
  const employerSubtitle = getEmployerTitle({ possessive: true })

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        backButton={{ onClick: goBack }}
        showExitPrompt={showExitPrompt}
        exitPromptTitle="Exit Payroll Search"
        exitPromptType={ExitPromptType.EMPLOYER}
      />
      <Body
        id={CONTAINER_ID}
        data-cy={DataAttribute.SCREEN_UNIFIED_SEARCH}
        className="relative h-full p-5"
        paddingTopOverride={8}
      >
        <H2 className="text mb-5 pr-1 text-left text-moss-950">Find the payroll provider for {employerSubtitle}</H2>
        <Input
          data-dd-privacy="allow"
          ref={inputRef}
          data-cy={DataAttribute.UNIFIED_SEARCH_INPUT}
          autoFocus
          name="platform-search"
          placeholder="e.g. Workday"
          label="Search for Payroll Provider"
          onChange={handleInputChange}
          tagType={null}
          value={searchQuery}
          className="mb-3"
        />
        {showLoader ? (
          <Loader />
        ) : (
          <Results query={searchQuery} items={items} kind={ItemKind.PLATFORM} handleSelectItem={handleSelectItem} />
        )}
      </Body>
      {hasSearched && <SearchFooter kind={ItemKind.PLATFORM} />}
    </>
  )
}
